import React, { useState, useCallback, memo } from 'react';
import { Skeleton } from "@/components/ui/skeleton";
import { OptimizedAudioPlayer } from './OptimizedAudioPlayer';
import { ImageZoom } from './ImageZoom';
import { MessageActionMenu } from './MessageActionMenu';
import { RepliedMessageDisplay } from './RepliedMessageDisplay';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ZoomIn, Reply } from 'lucide-react';
import { useSignedMediaUrl } from '@/hooks/useSignedMediaUrl';
import { Message, ReplyingToMessage } from '@/types/chat';
import { debugLogger } from '@/utils/debugLogger';
interface MessageBubbleProps {
  message: Message;
  isCurrentUser: (senderId: string) => boolean;
  partner?: {
    id: string;
    username: string;
    avatar: string;
  };
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
  showTimestamp?: boolean;
  onReply?: (message: ReplyingToMessage) => void;
  onDelete?: (messageId: string) => void;
}
const OptimizedMessageBubble = memo<MessageBubbleProps>(({
  message,
  isCurrentUser,
  partner,
  isFirstInGroup = false,
  isLastInGroup = false,
  showTimestamp = false,
  onReply,
  onDelete
}) => {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState<boolean>(false);
  const [isZoomOpen, setIsZoomOpen] = useState(false);

  // Use signed URL hook for media content
  const {
    signedUrl,
    isLoading: urlLoading,
    error: urlError
  } = useSignedMediaUrl({
    mediaUrl: message.media_url,
    contentType: message.content_type
  });
  const isUserMessage = isCurrentUser(message.sender_id);

  // Function to render message content with clickable links
  const renderMessageContent = useCallback((content: string | null) => {
    if (!content) return '';
    
    // URL regex to detect links
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = content.split(urlRegex);
    
    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-300 hover:text-blue-200 underline cursor-pointer"
            onClick={(e) => e.stopPropagation()} // Prevent triggering the action menu
          >
            {part}
          </a>
        );
      }
      return part;
    });
  }, []);

  // Log detalhado para debug das mensagens de reply
  React.useEffect(() => {
    if (message.reply_to_message_id) {
      debugLogger.debug(`[MessageBubble] Message ${message.id} should show reply to ${message.reply_to_message_id}`, {
        has_replied_message: !!message.replied_message,
        replied_message_id: message.replied_message?.id,
        replied_message_content: message.replied_message?.content,
        message_content: message.content
      });
    }
  }, [message]);
  const getBubbleStyles = useCallback(() => {
    let baseStyles = 'relative group p-3 shadow-lg backdrop-blur-sm border transition-all duration-200 select-none';
    if (isUserMessage) {
      baseStyles += ' bg-luxury-red/80 text-white border-none';
    } else {
      baseStyles += ' bg-white/5 text-white border border-white/10';
    }

    // Rounded corners based on position in group
    if (isFirstInGroup && isLastInGroup) {
      // Single message - all corners rounded
      baseStyles += ' rounded-2xl';
    } else if (isFirstInGroup) {
      // First in group
      baseStyles += isUserMessage ? ' rounded-2xl rounded-br-md' : ' rounded-2xl rounded-bl-md';
    } else if (isLastInGroup) {
      // Last in group
      baseStyles += isUserMessage ? ' rounded-2xl rounded-tr-md' : ' rounded-2xl rounded-tl-md';
    } else {
      // Middle of group
      baseStyles += isUserMessage ? ' rounded-l-2xl rounded-r-md' : ' rounded-r-2xl rounded-l-md';
    }
    return baseStyles;
  }, [isUserMessage, isFirstInGroup, isLastInGroup]);
  const handleImageLoad = useCallback(() => {
    setImageLoading(false);
  }, []);
  const handleImageError = useCallback(() => {
    setImageLoading(false);
    setImageError(true);
  }, []);
  const handleReply = useCallback((msg: Message) => {
    if (onReply) {
      const replyingToMessage: ReplyingToMessage = {
        id: msg.id,
        content: msg.content || '',
        content_type: msg.content_type,
        sender_id: msg.sender_id,
        sender_name: isUserMessage ? 'Você' : partner?.username || 'Usuário'
      };
      onReply(replyingToMessage);
    }
  }, [onReply, isUserMessage, partner?.username]);
  const handleDelete = useCallback((messageId: string) => {
    if (onDelete) {
      onDelete(messageId);
    }
  }, [onDelete]);

  const handleZoomOpen = useCallback(() => {
    setIsZoomOpen(true);
  }, []);

  // Don't render deleted messages
  if (message.deleted_at) {
    return <div className={`flex ${isUserMessage ? 'justify-end' : 'justify-start'} ${isLastInGroup ? 'mb-4' : 'mb-1'}`}>
        <div className={`max-w-[80%] ${isUserMessage ? 'order-2' : 'order-1'}`}>
          <div className="bg-white/5 text-white/50 border border-white/10 rounded-2xl p-3 text-sm italic">
            Mensagem apagada
          </div>
        </div>
      </div>;
  }
  return <div className={`flex items-end gap-1 ${isUserMessage ? 'justify-end' : 'justify-start'} ${isLastInGroup ? 'mb-4' : 'mb-1'}`}>
      {/* Timestamp for current user (left side) */}
      {isUserMessage && <div className="text-xs text-white/40 mb-1 min-w-[35px] text-right mx-[8px]">
          {new Date(message.created_at).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })}
        </div>}
      
      <div className={`max-w-[80%]`}>
        {/* Para mensagens de imagem, não usar MessageActionMenu */}
        {message.content_type === 'image' ? (
          <div className={getBubbleStyles()}>
            {/* Show replied message if this is a reply */}
            {message.replied_message && <>
                {debugLogger.debug(`[MessageBubble] Rendering RepliedMessageDisplay for message ${message.id}`, {
              replied_to: message.replied_message.id,
              replied_content: message.replied_message.content
            })}
                <RepliedMessageDisplay repliedMessage={message.replied_message} senderName={isCurrentUser(message.replied_message.sender_id) ? 'Você' : partner?.username} isCurrentUserMessage={isUserMessage} />
              </>}

            {message.content_type === 'image' && <div className="relative">
                {/* Container fixo para evitar layout shifts */}
                <div className="w-64 h-96 relative">
                  {(imageLoading || urlLoading) && <Skeleton className="w-64 h-96 rounded-md absolute inset-0" />}
                  {(imageError || urlError) && !urlLoading && <div className="text-luxury-red text-xs absolute inset-0 flex items-center justify-center">
                      Erro ao carregar imagem
                    </div>}
                  {signedUrl && !urlError && 
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <img 
                          src={signedUrl} 
                          alt="Imagem enviada" 
                          className="w-64 h-96 rounded-md object-cover cursor-pointer hover:opacity-90 transition-opacity absolute inset-0" 
                          onLoad={handleImageLoad} 
                          onError={handleImageError} 
                          style={{ display: imageLoading || urlLoading ? 'none' : 'block' }} 
                        />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent 
                        align="center" 
                        className="bg-black border border-white/20 rounded-lg shadow-xl z-50 min-w-[180px] p-1"
                      >
                        <DropdownMenuItem 
                          onClick={handleZoomOpen}
                          className="focus:bg-white/10 hover:bg-white/10 rounded-md p-0"
                        >
                          <div className="flex items-center gap-3 px-3 py-2 cursor-pointer text-white w-full text-left">
                            <ZoomIn className="h-4 w-4 text-white flex-shrink-0" />
                            <span className="text-sm">Dar Zoom na Imagem</span>
                          </div>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleReply(message)}
                          className="focus:bg-white/10 hover:bg-white/10 rounded-md p-0"
                        >
                          <div className="flex items-center gap-3 px-3 py-2 cursor-pointer text-white w-full text-left">
                            <Reply className="h-4 w-4 text-white flex-shrink-0" />
                            <span className="text-sm">Responder</span>
                          </div>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  }
                </div>
              </div>}
          </div>
        ) : (
          <MessageActionMenu message={message} isCurrentUser={isUserMessage} onReply={handleReply} onDelete={handleDelete}>
            <div className={getBubbleStyles()}>
              {/* Show replied message if this is a reply */}
              {message.replied_message && <>
                  {debugLogger.debug(`[MessageBubble] Rendering RepliedMessageDisplay for message ${message.id}`, {
                replied_to: message.replied_message.id,
                replied_content: message.replied_message.content
              })}
                  <RepliedMessageDisplay repliedMessage={message.replied_message} senderName={isCurrentUser(message.replied_message.sender_id) ? 'Você' : partner?.username} isCurrentUserMessage={isUserMessage} />
                </>}

              {message.content_type === 'text' && <p className="text-sm leading-relaxed break-words whitespace-pre-wrap">
                  {renderMessageContent(message.content)}
                </p>}
              
              {message.content_type === 'audio' && <div className="w-full">
                  <OptimizedAudioPlayer src={message.media_url} messageId={message.id} audioDuration={message.audio_duration || 0} darkIndicator={isUserMessage} className="w-full min-w-[200px]" />
                </div>}
            </div>
          </MessageActionMenu>
        )}
      </div>
      
      {/* Controlled ImageZoom for image messages */}
      {message.content_type === 'image' && signedUrl && (
        <ImageZoom 
          src={signedUrl} 
          alt="Imagem enviada" 
          conversationId={message.conversation_id}
          isOpen={isZoomOpen}
          onOpenChange={setIsZoomOpen}
        >
          <div />
        </ImageZoom>
      )}
      
      {/* Timestamp for partner (right side) */}
      {!isUserMessage && <div className="text-xs text-white/40 mb-1 min-w-[35px] text-left mx-2">
          {new Date(message.created_at).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })}
        </div>}
    </div>;
});
OptimizedMessageBubble.displayName = 'OptimizedMessageBubble';
export { OptimizedMessageBubble as MessageBubble };